import axios from 'axios';
import globalTheme from 'Assets/js/components/globalTheme';

const customerAccountsService = (() => {
  const namespace = '/wp-json/oss-shopify-customer-accounts/v1';
  const sessionCookieName = 'STYXKEY_customer_accounts_session=';

  const status = () => globalTheme.env('customer_accounts') || {};

  const hasSessionCookie = () => {
    if (typeof document === 'undefined' || typeof document.cookie !== 'string') {
      return false;
    }

    return document.cookie
      .split(';')
      .some(cookie => cookie.trim().startsWith(sessionCookieName));
  };

  const enabled = () => {
    const features = globalTheme.featureEnabled() || {};
    const flagged =
      features.user_login_flow === 'customer_accounts' ||
      features.user?.login_flow === 'customer_accounts';
    const readiness = status();

    return (
      flagged &&
      readiness.available === true &&
      readiness.enabled === true &&
      readiness.configured === true
    );
  };

  const endpoint = (path) => {
    const siteUrl = globalTheme.siteUrl ? globalTheme.siteUrl() : '';
    return `${siteUrl}${namespace}${path}`;
  };

  const login = async (returnTo = '/account/details/', email = '') => {
    if (!enabled()) {
      return false;
    }

    const response = await axios.get(endpoint('/login/start'), {
      params: {
        return_to: returnTo,
        email
      }
    });

    const authorizeUrl = response?.data?.authorize_url;
    if (authorizeUrl) {
      window.location.href = authorizeUrl;
      return true;
    }

    throw new Error(
      response?.data?.error || 'Customer account login is unavailable'
    );
  };

  const logout = async () => {
    if (!enabled()) {
      return false;
    }

    window.location.href = endpoint('/logout?redirect=1');
    return true;
  };

  const getMe = async () => {
    if (!enabled()) {
      return null;
    }

    if (!hasSessionCookie()) {
      return null;
    }

    const response = await axios.get(endpoint('/me'));
    return response?.data?.user || null;
  };

  const updateMe = async (user) => {
    if (!enabled()) {
      return null;
    }

    const response = await axios.post(endpoint('/me'), user);
    return response?.data || null;
  };

  const saveAddress = async (address) => {
    if (!enabled()) {
      return null;
    }

    const addressId = address?.id ? `/${encodeURIComponent(address.id)}` : '';
    const response = await axios.post(
      endpoint(`/addresses${addressId}`),
      address
    );
    return response?.data || null;
  };

  const deleteAddress = async (addressId) => {
    if (!enabled()) {
      return null;
    }

    const response = await axios.delete(
      endpoint(`/addresses/${encodeURIComponent(addressId)}`)
    );
    return response?.data || null;
  };

  const toggleWishlist = async (productCode) => {
    if (!enabled()) {
      return null;
    }

    const response = await axios.post(endpoint('/wishlist/toggle'), {
      productCode
    });
    return response?.data || null;
  };

  return {
    deleteAddress,
    enabled,
    getMe,
    hasSessionCookie,
    login,
    logout,
    saveAddress,
    status,
    toggleWishlist,
    updateMe
  };
})();

export default customerAccountsService;
